import {
  ApplePayFundingOption,
  ApplePayFundingOptionsResult,
  ApplePayFundingOptionsResultTypenames,
  ButtonComponentV2,
  CardNetwork,
  LimitInformation,
  MerchantCapability as GraphqlMerchantCapability,
} from '@varo/graphql-schema-types';
import { useMemo } from 'react';
import applePayFundingOptions from 'services/graphql/queries/MoveMoney/ApplePayFundingOptions.query';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { RequestPolicy, useQuery } from 'urql';

export interface ApplePayFundingErrorScreen {
  screenType: ErrorScreenType;
  messageHeadline: string;
  messageBody: string;
  primaryButton?: ButtonComponentV2;
}

export type ApplePayErrorType = 'not_eligible' | 'error' | null;

interface ApplePayFundingOptionsData {
  isEligible: boolean;
  cardNetworks: CardNetwork[];
  merchantCapabilities: GraphqlMerchantCapability[];
  fundingOptions: ApplePayFundingOption[];
  limits: {
    minAmount: number;
    maxAmount: number;
    limitInfo: string;
    transferLimitErrorMessage: string | null;
  };
  limitInformation: {
    dailyLimitInformation: LimitInformation | null;
    monthlyLimitInformation: LimitInformation | null;
    disclaimer: string | null;
  };
}

interface UseApplePayFundingOptionsReturn {
  fetching: boolean;
  errorScreen: ApplePayFundingErrorScreen | null;
  errorType: ApplePayErrorType;
  data: ApplePayFundingOptionsData;
}

const useApplePayFundingOptions = (
  requestPolicy: RequestPolicy = 'network-only',
  pause: boolean = false,
): UseApplePayFundingOptionsReturn => {
  const [{ data, error, fetching }] = useQuery<{
    applePayFundingOptions: ApplePayFundingOptionsResult;
  }>({
    query: applePayFundingOptions,
    requestPolicy,
    pause,
  });

  const responseError = useFormattedCombinedError(error);
  const applePayFundingOptionsData = data?.applePayFundingOptions;

  const typename = applePayFundingOptionsData?.__typename;
  const isSuccess =
    typename ===
    ApplePayFundingOptionsResultTypenames.ApplePayFundingOptionsSuccess;
  const isNotEligible =
    typename ===
    ApplePayFundingOptionsResultTypenames.ApplePayFundingOptionsNotEligible;
  const isError =
    typename ===
    ApplePayFundingOptionsResultTypenames.ApplePayFundingOptionsError;

  const successData = isSuccess ? applePayFundingOptionsData : undefined;
  const notEligibleData = isNotEligible
    ? applePayFundingOptionsData
    : undefined;
  const errorData = isError ? applePayFundingOptionsData : undefined;

  const errorScreen = useMemo(() => {
    if (responseError) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError.title,
        messageBody: responseError.message,
      };
    }
    if (errorData) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: errorData.title,
        messageBody: errorData.message,
      };
    }
    if (notEligibleData) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: notEligibleData.title,
        messageBody: notEligibleData.message,
        primaryButton: notEligibleData.button,
      };
    }
    return null;
  }, [responseError, errorData, notEligibleData]);

  const errorType: ApplePayErrorType = useMemo(() => {
    if (isNotEligible) {
      return 'not_eligible';
    }
    if (isError || responseError) {
      return 'error';
    }
    return null;
  }, [isNotEligible, isError, responseError]);

  const {
    cardNetworks = [],
    limitInfo = '',
    merchantCapabilities = [],
    minAmount,
    maxAmount,
    options: fundingOptions = [],
    transferLimitErrorMessage = null,
    dailyLimitInformation = null,
    monthlyLimitInformation = null,
    disclaimer = null,
  } = successData ?? {};

  return {
    fetching,
    errorScreen,
    errorType,
    data: {
      isEligible: isSuccess,
      cardNetworks,
      merchantCapabilities,
      fundingOptions,
      limits: {
        minAmount: Number(minAmount?.amount ?? 0),
        maxAmount: Number(maxAmount?.amount ?? 0),
        limitInfo,
        transferLimitErrorMessage,
      },
      limitInformation: {
        dailyLimitInformation,
        monthlyLimitInformation,
        disclaimer,
      },
    },
  };
};

export default useApplePayFundingOptions;
